import React, { useEffect, useState } from 'react';
import { Button, Modal, Table, Tag, Form, Input, InputNumber, Select, Popconfirm, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Breakpoint } from 'antd/es/_util/responsiveObserver';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { expenseAPI, staffAPI } from '../services/api';
import { formatCurrencySimple } from '../utils/currency';
import StudioNavBar from '../components/common/StudioNavBar';
import { useAuth } from '../context/AuthContext';

const categoryColors: Record<string, string> = {
  equipment: 'blue',
  travel: 'purple',
  studio: 'gold',
  other: 'gray',
};

const responsiveMd: Breakpoint[] = ['md'];
const responsiveLg: Breakpoint[] = ['lg'];

const Expenses: React.FC = () => {
  const { user, isStaff } = useAuth();
  const [expenses, setExpenses] = useState<any[]>([]);
  const [producers, setProducers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [viewingExpense, setViewingExpense] = useState<any | null>(null);
  const [editingExpense, setEditingExpense] = useState<any | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [form] = Form.useForm();

  // Check if producer can edit this expense (only if they spent it)
  const canEditExpense = (expense: any) => {
    if (!isStaff) return true; // Studio owners can edit all
    return expense.spent_by === Number(user?.id);
  };

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleView = (expense: any) => {
    setViewingExpense(expense);
  };

  const handleEdit = (expense: any) => {
    // Check if producer can edit
    if (isStaff && !canEditExpense(expense)) {
      message.error('You can only edit expenses you recorded');
      return;
    }
    console.log('🔵 handleEdit called with expense:', expense);
    setEditingExpense(expense);
    const formValues = {
      ...expense,
      spent_by: expense.spent_by ? Number(expense.spent_by) : undefined,
      expense_date: expense.expense_date ? dayjs(expense.expense_date) : null,
    };
    console.log('📝 Setting form values:', formValues);
    setTimeout(() => {
      form.setFieldsValue(formValues);
      console.log('✅ Form values set');
    }, 0);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await expenseAPI.deleteExpense(id);
      message.success('Expense deleted successfully');
      fetchExpenses();
    } catch (err) {
      message.error('Failed to delete expense');
    }
  };

  const columns: ColumnsType<any> = [
    { 
      title: 'ID', 
      dataIndex: 'custom_id', 
      key: 'custom_id', 
      sorter: true, 
      width: 100,
      render: (_: any, record: any) => record.custom_id || record.id 
    },
    { 
      title: 'Category', 
      dataIndex: 'category', 
      key: 'category',
      width: 110,
      render: (cat: string) => <Tag color={categoryColors[cat] || 'default'}>{cat}</Tag>, 
      filters: [
        { text: 'Equipment', value: 'equipment' },
        { text: 'Travel', value: 'travel' },
        { text: 'Studio', value: 'studio' },
        { text: 'Other', value: 'other' },
      ] 
    },
    { 
      title: 'Description', 
      dataIndex: 'description', 
      key: 'description',
      width: 150,
      ellipsis: true
    },
    { 
      title: 'Amount', 
      dataIndex: 'amount', 
      key: 'amount',
      width: 120,
      render: (v: any) => formatCurrencySimple(v)
    },
    { 
      title: 'Date', 
      dataIndex: 'expense_date', 
      key: 'expense_date', 
      sorter: true,
      width: 110,
      responsive: responsiveMd
    },
    { 
      title: 'Spent By', 
      dataIndex: 'spent_by_name', 
      key: 'spent_by_name', 
      sorter: true,
      width: 140,
      responsive: responsiveLg,
      ellipsis: true
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      fixed: 'right' as const,
      render: (_: any, record: any) => {
        const canEdit = canEditExpense(record);
        return (
          <div className="flex gap-1">
            {!canEdit ? (
              // Producers can only view expenses they didn't record
              <Button 
                icon={<EyeOutlined />} 
                onClick={() => handleView(record)}
                size="small"
              >
                View
              </Button>
            ) : (
              // Can edit and delete their own entries
              <>
                <Button 
                  type="primary" 
                  icon={<EditOutlined />} 
                  onClick={() => handleEdit(record)}
                  size="small"
                  className="hidden sm:inline-flex"
                >
                  Edit
                </Button>
                <Button 
                  type="primary" 
                  icon={<EditOutlined />} 
                  onClick={() => handleEdit(record)}
                  size="small"
                  className="sm:hidden"
                />
                <Popconfirm
                  title="Delete expense?"
                  description="Are you sure you want to delete this expense?"
                  onConfirm={() => handleDelete(record.id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button 
                    danger 
                    icon={<DeleteOutlined />}
                    size="small"
                  />
                </Popconfirm>
              </>
            )}
          </div>
        );
      }
    },
  ];

  useEffect(() => {
    fetchExpenses();
    fetchProducers();
  }, []);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const res = await expenseAPI.getExpenses();
      setExpenses(res.data);
    } catch (err) {
      message.error('Failed to fetch expenses');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducers = async () => {
    try {
      const res = await staffAPI.getStaff();
      setProducers(res.data);
    } catch (err) {
      message.error('Failed to fetch producers');
    }
  };

  const handleAdd = () => {
    setEditingExpense(null);
    form.resetFields();
    // For producers, automatically set spent_by to their own ID
    if (isStaff && user?.id) {
      form.setFieldsValue({ spent_by: Number(user.id) });
    }
    setShowModal(true);
  };

  const handleSave = async (values: any) => {
    try {
      // Check for duplicate custom_id if provided
      if (values.custom_id) {
        const duplicateExists = expenses.some((e: any) => 
          e.custom_id === values.custom_id && 
          (!editingExpense || e.id !== editingExpense.id)
        );
        
        if (duplicateExists) {
          message.error(`Expense with ID "${values.custom_id}" already exists in your studio`);
          return;
        }
      }
      
      // Only send the fields the backend expects
      // For producers, force spent_by to be their own ID
      const formattedValues = {
        custom_id: values.custom_id || null,
        description: values.description,
        amount: values.amount,
        category: values.category,
        spent_by: isStaff ? Number(user?.id) : (values.spent_by || null),
      };
      
      console.log('📤 Sending to backend:', formattedValues);
      
      if (editingExpense) {
        await expenseAPI.updateExpense(editingExpense.id, formattedValues);
      } else {
        await expenseAPI.createExpense(formattedValues);
      }
      setShowModal(false);
      form.resetFields();
      message.success('Expense saved successfully');
      fetchExpenses();
    } catch (err: any) {
      console.error('Save error:', err);
      
      // Handle backend validation error for duplicate custom_id
      if (err.response?.data?.custom_id) {
        message.error(err.response.data.custom_id[0]);
      } else {
        message.error('Failed to save expense');
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <StudioNavBar />
      <main className="mx-auto max-w-7xl px-4 py-6 sm:py-8 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="flex flex-col gap-3 sm:gap-4 sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Expenses</h1>
            <p className="mt-1 text-xs sm:text-sm text-slate-500">Capture studio spending and keep budgets on track.</p>
          </div>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAdd} 
            size="large"
            className="w-full sm:w-auto"
          >
            Add Expense
          </Button>
        </div>

        {/* Expenses Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <Table
              columns={columns}
              dataSource={expenses}
              loading={loading}
              rowKey="id"
              pagination={{ pageSize: 10, responsive: true }}
              scroll={{ x: 'max-content' }}
              size="middle"
            />
          </div>
        </div>
      </main>

      {/* Add/Edit Modal */}
      <Modal
        title={editingExpense ? 'Edit Expense' : 'Add Expense'}
        open={showModal}
        onCancel={() => {
          setShowModal(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText={editingExpense ? 'Update' : 'Create'}
        width={isMobile ? '95vw' : 600}
        style={isMobile ? { maxWidth: '95vw' } : undefined}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          preserve={false}
        >
          <Form.Item name="custom_id" label="Expense ID">
            <Input placeholder="Enter expense ID" size="large" />
          </Form.Item>
          <Form.Item name="category" label="Category" rules={[{ required: true, message: 'Please select category' }]}>
            <Select 
              placeholder="Select category" 
              size="large"
              options={[
                { value: 'utilities', label: 'Utilities' }, 
                { value: 'equipment', label: 'Equipment' }, 
                { value: 'rent', label: 'Rent' }, 
                { value: 'food', label: 'Food' }, 
                { value: 'other', label: 'Other' }
              ]} 
            />
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{ required: true, message: 'Please enter description' }]}>
            <Input placeholder="Enter description" size="large" />
          </Form.Item>
          <Form.Item name="amount" label="Amount" rules={[{ required: true, message: 'Please enter amount' }]}>
            <InputNumber 
              style={{ width: '100%' }} 
              size="large"
              prefix="UGX" 
              min={0} 
              precision={0} 
              placeholder="Enter amount" 
            />
          </Form.Item>
          <Form.Item name="spent_by" label="Spent By">
            <Select 
              placeholder="Select a producer"
              size="large"
              allowClear={!isStaff}
              disabled={isStaff}
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={producers.map(p => ({ 
                value: p.id, 
                label: `${p.first_name} ${p.last_name}` 
              }))}
            />
          </Form.Item>
          {isStaff && (
            <p className="text-xs text-gray-500 -mt-4 mb-4">
              As a producer, expenses are automatically assigned to you.
            </p>
          )}
        </Form>
      </Modal>

      {/* View Expense Modal (for read-only viewing) */}
      <Modal
        title="Expense Details"
        open={!!viewingExpense}
        onCancel={() => setViewingExpense(null)}
        footer={[
          <Button key="close" onClick={() => setViewingExpense(null)}>
            Close
          </Button>
        ]}
        width={isMobile ? '95vw' : 500}
        style={isMobile ? { maxWidth: '95vw' } : undefined}
      >
        {viewingExpense && (
          <div className="space-y-3">
            <div><strong>Expense ID:</strong> {viewingExpense.custom_id || viewingExpense.id}</div>
            <div><strong>Category:</strong> <Tag color={categoryColors[viewingExpense.category] || 'default'}>{viewingExpense.category}</Tag></div>
            <div><strong>Description:</strong> {viewingExpense.description}</div>
            <div><strong>Amount:</strong> {formatCurrencySimple(viewingExpense.amount)}</div>
            <div><strong>Date:</strong> {viewingExpense.expense_date}</div>
            <div><strong>Spent By:</strong> {viewingExpense.spent_by_name || 'N/A'}</div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Expenses;
